import type { ReactNode, Ref } from "react";

import { Button } from "@/components/ui/button";
import { PRESSED_CLASSES } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";

/**
 * The one "on" style for viewport toolbar toggles (shared with `IconButton pressed`).
 * Apply alongside `aria-pressed`.
 */
export const VIEWPORT_TOOL_PRESSED_CLASSES = PRESSED_CLASSES;

/** Uppercase section label inside viewport tool flyouts ("Units", "Drawing aids"). */
export const VIEWPORT_PANEL_EYEBROW_CLASS =
  "text-[10px] font-semibold uppercase tracking-wider text-muted-foreground";

const RAIL_BUTTON_BASE =
  "h-[60px] w-[60px] rounded-none border-x border-t border-b-0 border-border/70 bg-background/95 px-1 py-1.5 text-[9px] font-semibold uppercase tracking-widest shadow-none backdrop-blur flex-col gap-1 last:border-b";

// Long labels shrink so they fit the 60px rail button.
function getRailLabelClassName(label: string) {
  if (label.length >= 11) {
    return "text-[6px] tracking-wide";
  }

  if (label.length >= 9) {
    return "text-[7px] tracking-wider";
  }

  return "text-[8px] tracking-wider";
}

export interface ViewportToolsetHostItem {
  id: string;
  icon: ReactNode;
  label: string;
  active?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
  showArrow?: boolean;
  sublabel?: string;
  showExpandedIndicator?: boolean;
  flyout?: ReactNode;
  showFlyout?: boolean;
}

export function ViewportToolsetSubButton({
  icon,
  label,
  sublabel,
  sublabelAriaLabel,
  buttonClassName,
  active,
  disabled,
  buttonRef,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  sublabel?: ReactNode;
  sublabelAriaLabel?: string;
  buttonClassName?: string;
  active?: boolean;
  disabled?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  onClick: () => void;
}) {
  const labelClassName = getRailLabelClassName(label);
  const resolvedSublabelAriaLabel =
    typeof sublabel === "string" ? sublabel : sublabelAriaLabel;

  return (
    <Button
      ref={buttonRef}
      type="button"
      variant="ghost"
      disabled={disabled}
      aria-label={resolvedSublabelAriaLabel ? `${label}, ${resolvedSublabelAriaLabel}` : label}
      aria-pressed={Boolean(active)}
      onClick={onClick}
      className={cn(
        RAIL_BUTTON_BASE,
        "hover:bg-muted hover:text-foreground",
        VIEWPORT_TOOL_PRESSED_CLASSES,
        buttonClassName
      )}
    >
      <span className="flex items-center justify-center">{icon}</span>
      {sublabel ? (
        <span
          aria-hidden="true"
          className="flex w-full items-center justify-center gap-0.5 text-[6px] leading-none opacity-70"
        >
          {sublabel}
        </span>
      ) : null}
      <span className={cn("w-full text-center leading-tight break-words", labelClassName)}>
        {label}
      </span>
    </Button>
  );
}
